/**
 * ViewStore - MobX store for managing dashboard view state
 *
 * This store:
 * - Manages the current view selection (Organization, Group, Individual)
 * - Manages the time range selection
 * - Manages the active tab for dashboards
 * - Determines which options are enabled based on available data
 */

import { makeAutoObservable } from "mobx"
import { getFromStorage, StorageKeys, saveToStorage } from "../lib/localStorage"
import { logger } from "../lib/logger"

export type ViewType = "organization" | "group" | "individual"
export type TimeRange = "7days" | "14days" | "28days" | "8weeks" | "12weeks"

export class ViewStore {
  currentView: ViewType = "organization"
  timeRange: TimeRange = "7days"
  activeTab: string = "insights"

  constructor() {
    makeAutoObservable(this)
    // Load preferences from localStorage on initialization
    this.loadFromStorage()
  }

  /**
   * Load preferences from localStorage
   */
  private loadFromStorage(): void {
    const savedView = getFromStorage(StorageKeys.VIEW) as ViewType | null
    const savedTimeRange = getFromStorage(StorageKeys.TIME_RANGE) as TimeRange | null
    const savedTab = getFromStorage(StorageKeys.ACTIVE_TAB)

    if (savedView && ["organization", "group", "individual"].includes(savedView)) {
      this.currentView = savedView
    }
    if (
      savedTimeRange &&
      ["7days", "14days", "28days", "8weeks", "12weeks"].includes(savedTimeRange)
    ) {
      this.timeRange = savedTimeRange
    }
    if (savedTab) {
      this.activeTab = savedTab
    }
  }

  /**
   * Set the current view
   */
  setView(view: ViewType): void {
    logger.info(`Changing view to: ${view}`, { component: "ViewStore" })
    logger.track("view_changed", { view })
    this.currentView = view
    saveToStorage(StorageKeys.VIEW, view)
  }

  /**
   * Set the time range
   */
  setTimeRange(timeRange: TimeRange): void {
    logger.info(`Changing time range to: ${timeRange}`, { component: "ViewStore" })
    logger.track("time_range_changed", { timeRange })
    this.timeRange = timeRange
    saveToStorage(StorageKeys.TIME_RANGE, timeRange)
  }

  /**
   * Set the active tab
   */
  setActiveTab(tab: string): void {
    logger.info(`Changing active tab to: ${tab}`, { component: "ViewStore" })
    logger.track("tab_changed", { tab })
    this.activeTab = tab
    saveToStorage(StorageKeys.ACTIVE_TAB, tab)
  }

  /**
   * Check if the current view is organization
   */
  get isOrganizationView(): boolean {
    return this.currentView === "organization"
  }

  /**
   * Check if the current view is group
   */
  get isGroupView(): boolean {
    return this.currentView === "group"
  }

  /**
   * Check if the current view is individual
   */
  get isIndividualView(): boolean {
    return this.currentView === "individual"
  }

  /**
   * Reset to default view
   */
  reset(): void {
    this.currentView = "organization"
    this.timeRange = "7days"
    this.activeTab = "insights"
    logger.info("ViewStore reset", { component: "ViewStore" })
  }
}
